import { EVENT_NAMES } from '@freightapi/core/events/catalog'
import {
  Button,
  DataTable,
  type DataTableColumn,
  EmptyState,
  Field,
  formatDateTime,
  Sheet,
} from '@freightapi/ui'
import { createFileRoute } from '@tanstack/react-router'
import { type FormEvent, useCallback, useState } from 'react'
import { ResourceView } from '../components/ResourceView'
import {
  asApiError,
  type DeliveryFilters,
  fetchDeliveries,
  fetchDelivery,
  fetchWebhookEndpoints,
  replayDelivery,
  WEBHOOK_DELIVERY_STATUSES,
  type WebhookDelivery,
  type WebhookDeliveryStatus,
  type WebhookDeliveryView,
} from '../lib/api'
import { useConsoleI18n } from '../lib/locale'
import { useMode } from '../lib/mode'
import { useResource } from '../lib/resource'
import { useCan } from '../lib/session'

/**
 * The webhook delivery log (M3-06): what Cargofollow sent to your endpoints, what came back, and a
 * button to send it again.
 *
 * It reads `/v1/console/webhook-deliveries`, the session alias of the M1-14 log, so the filters and
 * the cursor on this screen are the ones documented for the API — a developer who learns this table
 * has learned the endpoint.
 *
 * Which delivery is open lives in the URL (`?delivery=whd_…`), like the tabs of the shipment page:
 * a failing delivery is something you paste into a chat message, and a sheet whose state is only in
 * React state cannot be linked to.
 */
export const Route = createFileRoute('/_app/webhooks/deliveries')({
  component: Deliveries,
  validateSearch: (search: Record<string, unknown>): { delivery?: string } =>
    typeof search.delivery === 'string' && search.delivery !== ''
      ? { delivery: search.delivery }
      : {},
})

interface FilterDraft {
  readonly status: WebhookDeliveryStatus | ''
  readonly endpoint_id: string
  readonly event_type: string
}

const NO_FILTERS: FilterDraft = { status: '', endpoint_id: '', event_type: '' }

function Deliveries() {
  const { t, locale } = useConsoleI18n()
  const { mode } = useMode()
  const { delivery: open } = Route.useSearch()
  const navigate = Route.useNavigate()
  const [draft, setDraft] = useState<FilterDraft>(NO_FILTERS)
  const [applied, setApplied] = useState<FilterDraft>(NO_FILTERS)
  const [trail, setTrail] = useState<readonly string[]>([])
  const [trailMode, setTrailMode] = useState(mode)
  const cursor = trail.at(-1)

  // The other mode is another log, so a cursor from this one means nothing there. Adjusted while
  // rendering rather than in an effect: this is derived state, and a render that first paints a
  // page of the other mode would be a lie on screen.
  if (trailMode !== mode) {
    setTrailMode(mode)
    setTrail([])
  }

  const loadEndpoints = useCallback(
    (signal: AbortSignal) => fetchWebhookEndpoints(mode, signal),
    [mode],
  )
  const endpoints = useResource(loadEndpoints)

  const load = useCallback(
    (signal: AbortSignal) =>
      fetchDeliveries(
        mode,
        { ...toFilters(applied), ...(cursor === undefined ? {} : { cursor }) },
        signal,
      ),
    [mode, applied, cursor],
  )
  const { state, reload } = useResource(load)

  /** The endpoint a delivery went to, by url; an endpoint that was deleted keeps its id. */
  const urlOf = (id: string): string =>
    (endpoints.state.status === 'ready'
      ? endpoints.state.value.endpoints.find((endpoint) => endpoint.id === id)?.url
      : undefined) ?? id

  function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    // A new filter is a new result set: the cursors of the old one point into nothing.
    setTrail([])
    setApplied(draft)
  }

  const columns: readonly DataTableColumn<WebhookDelivery>[] = [
    {
      key: 'event',
      header: t('deliveries.column.event'),
      cell: (row) => (
        <>
          <button
            type="button"
            onClick={() => void navigate({ search: { delivery: row.id }, replace: true })}
            className="text-left font-medium text-body text-brand-700 underline"
          >
            {row.event_type}
          </button>
          <span className="block font-mono text-caption text-muted">{row.id}</span>
        </>
      ),
    },
    {
      key: 'endpoint',
      header: t('deliveries.column.endpoint'),
      cell: (row) => (
        <span className="break-all text-caption text-muted">{urlOf(row.endpoint_id)}</span>
      ),
    },
    {
      key: 'status',
      header: t('deliveries.column.status'),
      cell: (row) => <span className="text-ink">{t(`deliveries.status.${row.status}`)}</span>,
    },
    {
      key: 'attempts',
      header: t('deliveries.column.attempts'),
      numeric: true,
      cell: (row) =>
        t('deliveries.attempts', { attempt: String(row.attempt), max: String(row.max_attempts) }),
    },
    {
      key: 'response',
      header: t('deliveries.column.response'),
      numeric: true,
      cell: (row) =>
        row.response_status === null ? (
          <span className="text-danger">{row.error ?? t('deliveries.no_response')}</span>
        ) : (
          <span className="text-ink">{row.response_status}</span>
        ),
    },
    {
      key: 'moment',
      header: t('deliveries.column.moment'),
      cell: (row) => (
        <span className="text-caption text-muted">{formatDateTime(row.created_at, locale)}</span>
      ),
    },
  ]

  return (
    <>
      <div className="flex flex-col gap-6">
        <p className="text-body text-muted">{t('deliveries.intro')}</p>

        <form
          className="flex flex-wrap items-end gap-4 rounded-card border border-line p-4"
          onSubmit={onSubmit}
        >
          <Field label={t('deliveries.filter.status')}>
            {({ id }) => (
              <select
                id={id}
                value={draft.status}
                onChange={(event) =>
                  setDraft({ ...draft, status: event.target.value as WebhookDeliveryStatus | '' })
                }
                className="min-h-touch rounded-card border border-line bg-surface px-2 text-body text-ink"
              >
                <option value="">{t('deliveries.filter.status.any')}</option>
                {WEBHOOK_DELIVERY_STATUSES.map((status) => (
                  <option key={status} value={status}>
                    {t(`deliveries.status.${status}`)}
                  </option>
                ))}
              </select>
            )}
          </Field>
          <Field label={t('deliveries.filter.endpoint')}>
            {({ id }) => (
              <select
                id={id}
                value={draft.endpoint_id}
                onChange={(event) => setDraft({ ...draft, endpoint_id: event.target.value })}
                className="min-h-touch max-w-72 rounded-card border border-line bg-surface px-2 text-body text-ink"
              >
                <option value="">{t('deliveries.filter.endpoint.any')}</option>
                {endpoints.state.status === 'ready'
                  ? endpoints.state.value.endpoints.map((endpoint) => (
                      <option key={endpoint.id} value={endpoint.id}>
                        {endpoint.url}
                      </option>
                    ))
                  : null}
              </select>
            )}
          </Field>
          <Field label={t('deliveries.filter.event')}>
            {({ id }) => (
              <select
                id={id}
                value={draft.event_type}
                onChange={(event) => setDraft({ ...draft, event_type: event.target.value })}
                className="min-h-touch rounded-card border border-line bg-surface px-2 text-body text-ink"
              >
                <option value="">{t('deliveries.filter.event.any')}</option>
                {EVENT_NAMES.map((name) => (
                  <option key={name} value={name}>
                    {name}
                  </option>
                ))}
              </select>
            )}
          </Field>
          <div className="flex gap-2">
            <Button type="submit">{t('filter.apply')}</Button>
            <Button
              type="button"
              variant="ghost"
              onClick={() => {
                setDraft(NO_FILTERS)
                setApplied(NO_FILTERS)
                setTrail([])
              }}
            >
              {t('filter.reset')}
            </Button>
          </div>
        </form>

        <ResourceView state={state}>
          {(page) => (
            <>
              <DataTable
                caption={t('deliveries.table.caption')}
                columns={columns}
                rows={page.data}
                rowKey={(row) => row.id}
                selectedKey={open}
                empty={
                  <EmptyState
                    title={t('deliveries.empty.title')}
                    description={t('deliveries.empty.body')}
                  />
                }
              />
              <div className="flex gap-2">
                <Button
                  variant="ghost"
                  disabled={trail.length === 0}
                  onClick={() => setTrail((followed) => followed.slice(0, -1))}
                >
                  {t('page.previous')}
                </Button>
                <Button
                  variant="ghost"
                  disabled={page.next_cursor === null}
                  onClick={() =>
                    setTrail((followed) =>
                      page.next_cursor === null ? followed : [...followed, page.next_cursor],
                    )
                  }
                >
                  {t('page.next')}
                </Button>
              </div>
            </>
          )}
        </ResourceView>
      </div>

      {open === undefined ? null : (
        <DeliverySheet
          id={open}
          onClose={() => void navigate({ search: {}, replace: true })}
          onReplayed={reload}
        />
      )}
    </>
  )
}

/**
 * The detail: the exact bytes that were sent, the answer that came back, and the timeline of every
 * send of this event to this endpoint.
 *
 * The replay writes its own answer back into this sheet instead of reloading. That is not an
 * optimisation: the API replies with the new timeline, and a reload would race the queue that is
 * already delivering the replay — the "new attempt" the ticket asks to see would sometimes be there
 * and sometimes not.
 */
function DeliverySheet({
  id,
  onClose,
  onReplayed,
}: {
  readonly id: string
  readonly onClose: () => void
  readonly onReplayed: () => void
}) {
  const { t, locale } = useConsoleI18n()
  const { mode } = useMode()
  const can = useCan()
  const [replayed, setReplayed] = useState<WebhookDeliveryView | undefined>(undefined)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | undefined>(undefined)

  const load = useCallback((signal: AbortSignal) => fetchDelivery(id, mode, signal), [id, mode])
  const { state } = useResource(load)

  async function onReplay() {
    setBusy(true)
    setError(undefined)
    try {
      setReplayed(await replayDelivery(id, mode))
      // The table behind the sheet has a row fewer than the log does now.
      onReplayed()
    } catch (cause) {
      const failure = asApiError(cause)
      setError(failure.problem?.detail ?? t(failure.messageKey))
    } finally {
      setBusy(false)
    }
  }

  return (
    <Sheet
      open
      title={t('deliveries.detail.title')}
      closeLabel={t('button.close')}
      onClose={onClose}
    >
      <ResourceView state={state}>
        {(loaded) => {
          const view = replayed ?? loaded
          return (
            <div className="flex flex-col gap-5">
              <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 text-body">
                <Row label={t('deliveries.column.event')} value={view.delivery.event_type} />
                <Row label={t('deliveries.detail.event_id')} value={view.delivery.event_id} mono />
                <Row label={t('deliveries.column.status')}>
                  {t(`deliveries.status.${view.delivery.status}`)}
                </Row>
              </dl>

              <Section title={t('deliveries.detail.timeline')}>
                <ol className="flex list-none flex-col gap-2 p-0">
                  {view.attempts.map((attempt, index) => (
                    <li
                      key={attempt.id}
                      className="flex flex-col gap-0.5 rounded-card border border-line px-3 py-2"
                    >
                      <span className="font-medium text-body text-ink">
                        {t('deliveries.detail.attempt', {
                          number: String(index + 1),
                          status: t(`deliveries.status.${attempt.status}`),
                        })}
                      </span>
                      <span className="text-caption text-muted">
                        {`${formatDateTime(attempt.created_at, locale)} · ${t(
                          'deliveries.attempts',
                          {
                            attempt: String(attempt.attempt),
                            max: String(attempt.max_attempts),
                          },
                        )}`}
                      </span>
                      <span className="text-caption text-muted">
                        {attempt.response_status === null
                          ? (attempt.error ?? t('deliveries.no_response'))
                          : t('deliveries.detail.answered', {
                              status: String(attempt.response_status),
                              duration: String(attempt.duration_ms ?? 0),
                            })}
                      </span>
                    </li>
                  ))}
                </ol>
              </Section>

              <Section title={t('deliveries.detail.request_headers')}>
                <Pre
                  text={Object.entries(view.delivery.request_headers)
                    .map(([name, value]) => `${name}: ${value}`)
                    .join('\n')}
                />
              </Section>
              <Section title={t('deliveries.detail.request_body')}>
                <Pre text={view.delivery.request_body} />
              </Section>
              <Section title={t('deliveries.detail.response_body')}>
                <Pre text={view.delivery.response_body} />
              </Section>

              {error === undefined ? null : (
                <p role="alert" className="text-body text-danger">
                  {error}
                </p>
              )}
              {can('webhooks:write') ? (
                <Button busy={busy} busyLabel={t('loading')} onClick={() => void onReplay()}>
                  {t('deliveries.replay')}
                </Button>
              ) : null}
            </div>
          )
        }}
      </ResourceView>
    </Sheet>
  )
}

function Row({
  label,
  value,
  mono = false,
  children,
}: {
  readonly label: string
  readonly value?: string
  readonly mono?: boolean
  readonly children?: React.ReactNode
}) {
  return (
    <>
      <dt className="text-caption text-muted">{label}</dt>
      <dd className={mono ? 'm-0 break-all font-mono text-caption text-ink' : 'm-0 text-ink'}>
        {children ?? value}
      </dd>
    </>
  )
}

function Section({
  title,
  children,
}: {
  readonly title: string
  readonly children: React.ReactNode
}) {
  return (
    <section className="flex flex-col gap-2">
      <h3 className="font-semibold text-body text-ink">{title}</h3>
      {children}
    </section>
  )
}

/** A body or a header block, verbatim. Never parsed: what was sent is what is shown. */
function Pre({ text }: { readonly text: string | null }) {
  const { t } = useConsoleI18n()
  if (text === null || text === '') {
    return <p className="text-caption text-muted italic">{t('deliveries.detail.none')}</p>
  }
  return (
    <pre className="code-block max-h-64 overflow-auto rounded-card border border-line bg-canvas p-3 text-caption text-ink">
      {text}
    </pre>
  )
}

function toFilters(draft: FilterDraft): DeliveryFilters {
  return {
    ...(draft.status === '' ? {} : { status: draft.status }),
    ...(draft.endpoint_id === '' ? {} : { endpoint_id: draft.endpoint_id }),
    ...(draft.event_type === '' ? {} : { event_type: draft.event_type }),
  }
}
